<template>
  <SfDrawer :open="isOpen" :title="title" @close="close">
    <div
      v-if="loading"
      class="tw-flex tw-flex-col tw-gap-4"
      data-testid="group-panel-loading"
    >
      <SfSkeletonLoader
        v-for="placeholder in GROUP_PANEL_SKELETON_ROWS"
        :key="placeholder"
        width="100%"
        height="4rem"
      />
    </div>

    <div
      v-else-if="loadError"
      class="tw-text-sm tw-text-sf-slate-dark-1"
      role="alert"
      data-testid="group-panel-load-error"
    >
      {{ I18n.t('group.panel.load_error') }}
    </div>

    <div v-else class="tw-flex tw-flex-col tw-gap-4">
      <PanelCard :title="I18n.t('group.panel.details_title')">
        <div class="tw-w-80">
          <SfInput
            :id="`${idPrefix}-name`"
            v-model="name"
            :label="I18n.t('common.name')"
            :placeholder="I18n.t('group.panel.name_placeholder')"
            :has-error="nameErrors.length > 0"
            :error-text="nameErrors"
            data-testid="group-panel-name"
          />
        </div>
      </PanelCard>

      <GroupItemList
        :list="GROUP_LISTS.member"
        :items="members"
        :icon-size="GROUP_MEMBER_AVATAR_SIZE"
        @add="addMember"
        @remove="removeMember"
      >
        <template #icon="{ item }">
          <SfUserAvatar :name="item.name" size="md" />
        </template>
        <template #subtitles="{ item }">
          <SfInpageListItemSubtitle>{{ item.email }}</SfInpageListItemSubtitle>
        </template>
      </GroupItemList>

      <GroupItemList
        :list="GROUP_LISTS.file"
        :items="files"
        @add="addFile"
        @remove="removeFile"
      >
        <template #icon>
          <span class="fa fa-building tw-text-sf-slate" aria-hidden="true" />
        </template>
      </GroupItemList>
    </div>

    <template #footer>
      <p
        v-if="saveError"
        class="tw-m-0 tw-mr-auto tw-text-sm tw-text-sf-danger"
        role="alert"
        data-testid="group-panel-save-error"
      >
        {{ saveError }}
      </p>

      <SfButtonDs
        v-if="!creating"
        variant="danger"
        :disabled="loading || loadError || saving || deleting"
        data-testid="group-panel-delete"
        @click="confirmingDelete = true"
      >
        {{ I18n.t('group.panel.delete') }}
      </SfButtonDs>

      <SfButtonDs
        variant="primary"
        :disabled="loading || loadError || saving || deleting"
        :loading="saving"
        data-testid="group-panel-save"
        @click="onSave"
      >
        {{ I18n.t('group.panel.save_changes') }}
      </SfButtonDs>
    </template>
  </SfDrawer>

  <SfModal
    v-if="confirmingDelete"
    size="s"
    data-testid="group-panel-delete-modal"
    @close="confirmingDelete = false"
  >
    <template #title>
      {{ I18n.t('group.panel.delete_title') }}
    </template>
    <template #body>
      <p class="tw-m-0 tw-text-center tw-text-sm tw-text-sf-slate-dark-1">
        {{ I18n.t('group.panel.delete_confirm', { name }) }}
      </p>
    </template>
    <template #footer>
      <div class="tw-flex tw-justify-center tw-gap-2">
        <SfButtonDs variant="secondary" @click="confirmingDelete = false">
          {{ I18n.t('common.cancel') }}
        </SfButtonDs>
        <SfButtonDs
          variant="danger"
          :loading="deleting"
          data-testid="group-panel-delete-confirm"
          @click="onRemove"
        >
          {{ I18n.t('group.panel.delete') }}
        </SfButtonDs>
      </div>
    </template>
  </SfModal>
</template>

<script setup lang="ts">
import { computed, useId, watch } from 'vue'
import { storeToRefs } from 'pinia'

import {
  GROUP_MEMBER_AVATAR_SIZE,
  GROUP_PANEL_SKELETON_ROWS,
  QUERY_KEYS,
} from '@app/application/user-management/constants'
import { GROUP_LISTS } from '@app/application/user-management/group-lists'
import { toRecordId } from '@app/application/user-management/utils/list-query'
import { useGroupPanelStore } from '@app/application/user-management/stores/group-panel'
import useI18n from '@app/shared/vue/composables/use-I18n'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'
import GroupItemList from '@app/application/user-management/components/groups/group-item-list.vue'
import PanelCard from '@app/application/user-management/components/panel-card.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfDrawer from '@app/shared/vue/components/sf-drawer/sf-drawer.vue'
import SfInpageListItemSubtitle from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item-subtitle.vue'
import SfInput from '@app/shared/vue/components/sf-input/sf-input.vue'
import SfModal from '@app/shared/vue/components/sf-modal/sf-modal.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import SfUserAvatar from '@app/shared/vue/components/sf-user-avatar/sf-user-avatar.vue'

defineOptions({ name: 'GroupPanel' })

const emit = defineEmits<{ saved: [] }>()

const I18n = useI18n()
const idPrefix = useId()
const store = useGroupPanelStore()

const {
  confirmingDelete,
  creating,
  deleting,
  files,
  isOpen,
  loadError,
  loading,
  members,
  name,
  nameErrors,
  openGroupId,
  saveError,
  saving,
} = storeToRefs(store)
const { addFile, addMember, close, removeFile, removeMember } = store

// A new group has no id yet, so only an existing one goes in the URL.
const linkedGroupId = useUrlStorage<number | null>(
  QUERY_KEYS.GROUP,
  null,
  (id) => (id === null ? '' : String(id)),
  { deserialize: toRecordId }
)

const title = computed(() =>
  creating.value ? I18n.t('group.new_group') : name.value
)

const finish = (): void => {
  emit('saved')
  close()
}

const onSave = async (): Promise<void> => {
  if (await store.save()) finish()
}

const onRemove = async (): Promise<void> => {
  if (await store.remove()) finish()
}

watch(
  linkedGroupId,
  (id) => {
    if (id === openGroupId.value) return

    if (id === null) {
      close()
    } else {
      void store.open(id)
    }
  },
  { immediate: true }
)

watch(openGroupId, (id) => {
  linkedGroupId.value = id
})
</script>
